import React, {useState} from "react";
import { csv, isStaticSite } from '../electron-modules';
import ListItem from "@material-ui/core/ListItem";
import CloudIcon from "@material-ui/icons/Cloud";
import PanoramaIcon from '@material-ui/icons/Panorama';
import PanoramaOutlinedIcon from '@material-ui/icons/PanoramaOutlined';
import ListItemText from "@material-ui/core/ListItemText";
import {Checkbox, ListItemSecondaryAction, Menu, MenuItem, Typography} from "@material-ui/core";
import LinearProgress from '@material-ui/core/LinearProgress';
import Box from '@material-ui/core/Box';
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import ErrorIcon from '@material-ui/icons/Error';
import ImportExportIcon from '@material-ui/icons/ImportExport';
import Satellite from '@material-ui/icons/Satellite';
import {PromptDialog, TextDialog} from "../dialogs";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {AssetType, toggleVisibility, changeName, deleteAsset} from "../redux/assetsSlice";
import {useSelector, useDispatch} from 'react-redux';
import { exportDialogCSV } from '../asset-utils';
import {DenseIcon} from '../dense-icon';
import clsx from 'clsx';

const initialState = {
  mouseX: null,
  mouseY: null,
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    itemList: {
      paddingLeft: theme.spacing(4),
      display: "inline-block"
    },
    itemText: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    cloudMargins: {
      margin: '0px 20px 0px 0px'
    },
    assetMissing: {
      color: theme.palette.error.main,
      fontWeight: "bold"
    },
    assetInfo: {
      display: 'flex',
      margin: '0px'
    },
    assetCheckbox: {
      position: "relative",
      transform: "translateY(0%)"
    },
    checkboxPadding: {
      padding: "0px"
    },
    percentLabel: {
      textAlign: "right"
    }
  }),
);

function LinearProgressWithLabel(props) {
  const {value} = props;

  const classes = useStyles();
  const visible = (value !== undefined) && (value < 100);
  const percent = value !== undefined ? value : 0;

  return (
    <React.Fragment>
      {visible && (<Box display="flex" alignItems="center">
        <Box width="100%" mr={1}>
          <LinearProgress variant="determinate" color="secondary" value={percent}/>
        </Box>
        <Box minWidth={35} className={classes.percentLabel}>
          <Typography variant="body2" color="textSecondary">
            {`${value}%`}
          </Typography>
        </Box>
      </Box>)}
    </React.Fragment>
  );
}

export function AssetItem(props) {
  const {assetID, viewer} = props;

  const dispatch = useDispatch();
  const asset = useSelector(state => state.assets.find(asset => asset.id === assetID));
  const classes = useStyles();

  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [menuState, setMenuState] = useState<{
    mouseX: null | number;
    mouseY: null | number;
  }>(initialState);

  const isPanoramic = asset.type === AssetType.Panoramic;
  const isPlanar = asset.type === AssetType.Planar;
  const isEncompassCloud = asset.type === AssetType.Encompass;
  const isRawPointcloud = asset.type === AssetType.LAS;
  const isOrtho = asset.type === AssetType.OrthoMosaic;

  const isCamera = isPanoramic || isPlanar;
  const isPointCloud = isRawPointcloud || isEncompassCloud;

  const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setMenuState({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  };

  const handleMenuClose = () => {
    setMenuState(initialState);
  };

  const handleRenameCancel = () => {
    setRenameOpen(false);
  };

  const handleRenameSubmit = name => {
    dispatch(changeName({assetID: asset.id, name}));
    setRenameOpen(false);
  };

  const handleRenameClick = () => {
    setMenuState(initialState);
    setRenameOpen(true);
  };

  const handleDeleteClick = () => {
    setMenuState(initialState);
    setDeleteOpen(true);
  };

  const handleAssetExport = (asset) => {
    setMenuState(initialState);
    const outputName = `${asset.name} corrected`;
    exportDialogCSV(outputName).then(csvPath => {
      if (!csvPath) {
        return;
      }

      let csvOut = [];
      csvOut.push(['Filename',
        'X', 'Y', 'Z',
        'Roll', 'Pitch', 'Yaw'
      ]);

      const toExport = viewer.getCamerasForCSV(asset.id);
      toExport.forEach(camera => {
        let position = camera.position.value;
        let rotation = camera.rotation.eulerDegrees;
        csvOut.push([
          camera.name,
          position.x, position.y, position.z,
          rotation.x, rotation.y, rotation.z
        ]);
      });

      csv.writeToPath(csvPath, csvOut)
        .on('error', err => console.error(err))
        .on('finish', () => console.log('Done writing.'));
    });
  };

  const onAssetDelete = () => {
    dispatch(deleteAsset(asset.id));
  };

  const handleDeleteCancel = () => {
    setDeleteOpen(false);
  };

  const handleAssetClick = (event) => {
    const isCheckBox = (event.target.type === "checkbox");
    if (isCheckBox) {
      return;
    }

    if (isOrtho && !isCheckBox) {
      viewer?.snapToOrtho(asset.id);
    } else if (isPointCloud && !isCheckBox) {
      viewer?.snapToPointCloud(asset.id);
    } else if (isCamera) {
      viewer?.snapToImages(asset.id);
    }
  };

  const handleDragStart = (event) => {
    event.dataTransfer.setData('text/plain', asset.id);
  };

  const handleVisibilityClick = () => {
    dispatch(toggleVisibility(asset.id));
  };

  const menuOpen = (menuState.mouseY !== null) && !isStaticSite;
  const anchorPosition = menuState.mouseY !== null && menuState.mouseX !== null
    ? { top: menuState.mouseY, left: menuState.mouseX }
    : undefined;

  const cloudPercent = isPointCloud ? viewer.getCloudPercent(asset.id) : 0.0;
  const cloudMissing = isPointCloud ? viewer.getCloudMissing(asset.id) : false;
  const canExportCSV = isPanoramic && !isStaticSite;
  const assetMissing = cloudMissing;

  return (
    <div>
      <ListItem
        className={classes.itemList}
        onContextMenu={handleContextMenu}
        button
        draggable
        onDragStart={handleDragStart}
        onClick={handleAssetClick}
      >
        <div className={classes.assetInfo}>
          <DenseIcon centered={isCamera}>
            <React.Fragment>
              {(isPanoramic) && (<PanoramaIcon fontSize="small"/>)}
              {(isPlanar) && (<PanoramaOutlinedIcon fontSize="small"/>)}
              {(isPointCloud && !cloudMissing) && (<CloudIcon fontSize="small"/>)}
              {(isPointCloud && cloudMissing) && (<ErrorIcon color="error" fontSize="small"/>)}
              {(isOrtho) && (<Satellite fontSize="small"/>)}
            </React.Fragment>
          </DenseIcon>
          <ListItemText
            className={clsx(classes.itemText, {
              [classes.cloudMargins]: !isCamera,
              [classes.assetMissing]: assetMissing
            })}
            primary={isCamera ? `(${asset.data.length}) ${asset.name}`: asset.name}
            title={assetMissing ? "File not found" : asset.name}
          />
          <ListItemSecondaryAction
            className={classes.assetCheckbox}
          >
            {!isCamera && (<Checkbox
              edge="end"
              size="small"
              className={classes.checkboxPadding}
              onChange={handleVisibilityClick}
              checked={asset.visible}
            />)}
          </ListItemSecondaryAction>
        </div>

        {isRawPointcloud && (<div>
          <LinearProgressWithLabel
            value={cloudPercent}
          />
        </div>)}

      </ListItem>

      <Menu
        keepMounted
        open={menuOpen}
        onClose={handleMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition}
      >
        {/* Export csv */}
        {canExportCSV && <MenuItem onClick={() => {
          handleAssetExport(asset);
        }}>
          <DenseIcon>
            <ImportExportIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">Export Corrected CSV</Typography>
        </MenuItem>}

        {/* Rename asset */}
        <MenuItem onClick={handleRenameClick}>
          <DenseIcon>
            <EditIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">Rename</Typography>
        </MenuItem>

        {/* Delete asset */}
        <MenuItem onClick={handleDeleteClick}>
          <DenseIcon>
            <DeleteIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">Remove From Project</Typography>
        </MenuItem>
      </Menu>

      {/* Discard measurements dialog */}
      <PromptDialog
        open={deleteOpen}
        onCancel={handleDeleteCancel}
        onSubmit={() => {
          onAssetDelete();
        }}
        title="Delete asset?"
        prompt={`Do you want to delete "${asset.name}"?`}
        button="Delete"
      />

      {/* Rename Dialog */}
      <TextDialog
        open={renameOpen}
        onCancel={handleRenameCancel}
        onSubmit={handleRenameSubmit}
        title="Rename Item"
        prompt="Enter new name"
        default={asset.name}
        label="Item Name"
      />
    </div>
  );
}